/**
 * An activity's checklists.
 *
 * The note it carries is not decoration: a checklist made from a template is a
 * COPY. Editing the template afterwards does not rewrite work already under
 * way, and a rep half through an installation should be able to see that
 * promise rather than discover it.
 */
import { useMutation, useQuery } from '@apollo/client';
import { ListChecks, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '@meshhub/design-tokens/ui';
import {
  ADD_CHECKLIST_ITEM,
  CHECKLISTS_QUERY,
  CHECKLIST_TEMPLATES_QUERY,
  CREATE_CHECKLIST,
  TOGGLE_CHECKLIST_ITEM,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import { CheckboxField, SelectField, TextField } from '../FormKit.web.jsx';

export function ChecklistPanel({ activityId }) {
  const [templateId, setTemplateId] = useState('');
  const [newItem, setNewItem] = useState({});

  const { data, refetch } = useQuery(CHECKLISTS_QUERY, {
    variables: { ownerType: 'ACTIVITY', ownerId: activityId },
    skip: !activityId,
  });
  const { data: templateData } = useQuery(CHECKLIST_TEMPLATES_QUERY);

  const [createChecklist] = useMutation(CREATE_CHECKLIST, { onCompleted: () => refetch() });
  const [toggleItem] = useMutation(TOGGLE_CHECKLIST_ITEM);
  const [addItem] = useMutation(ADD_CHECKLIST_ITEM, { onCompleted: () => refetch() });

  const checklists = data?.checklists ?? [];
  const templates = templateData?.checklistTemplates ?? [];

  return (
    <section className="flex flex-col gap-3 rounded border border-border p-3">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <ListChecks size={15} aria-hidden="true" />
        {S.activity.checklists}
      </h3>

      {checklists.map((checklist) => {
        const done = checklist.items.filter((item) => item.isDone).length;
        return (
          <div key={checklist.id} className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{checklist.title}</span>
              <span className="text-xs text-text-muted" dir="ltr">
                {done}/{checklist.items.length}
              </span>
            </div>
            {checklist.items.map((item) => (
              <CheckboxField
                key={item.id}
                label={item.title}
                checked={item.isDone}
                onChange={(event) =>
                  toggleItem({
                    variables: { itemId: item.id, isDone: event.target.checked },
                  })
                }
              />
            ))}
            <div className="flex items-end gap-2">
              <TextField
                label={S.checklists.itemTitle}
                value={newItem[checklist.id] ?? ''}
                onChange={(event) =>
                  setNewItem((current) => ({ ...current, [checklist.id]: event.target.value }))
                }
              />
              <Button
                type="button"
                disabled={!(newItem[checklist.id] ?? '').trim()}
                onClick={async () => {
                  await addItem({
                    variables: {
                      checklistId: checklist.id,
                      title: newItem[checklist.id].trim(),
                    },
                  });
                  setNewItem((current) => ({ ...current, [checklist.id]: '' }));
                }}
              >
                <Plus size={14} aria-hidden="true" />
              </Button>
            </div>
          </div>
        );
      })}

      <div className="flex items-end gap-2">
        <SelectField
          label={S.activity.fromTemplate}
          value={templateId}
          onChange={(event) => setTemplateId(event.target.value)}
        >
          <option value="">{S.activity.blankChecklist}</option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.title}
            </option>
          ))}
        </SelectField>
        <Button
          type="button"
          onClick={() =>
            createChecklist({
              variables: {
                ownerType: 'ACTIVITY',
                ownerId: activityId,
                templateId: templateId || null,
                title: templateId ? null : S.activity.checklists,
              },
            })
          }
        >
          <Plus size={14} aria-hidden="true" />
          {S.activity.addChecklist}
        </Button>
      </div>

      <p className="text-xs text-text-muted">{S.checklists.copyNote}</p>
    </section>
  );
}
